import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Aviso,
  Botao,
  CampoDeSelecao,
  Modal,
  formatarMoeda,
  useDialogo,
} from '../../../componentes/ui';
import { lerComoBase64 } from '../../../servicos/arquivo';
import { ErroApi } from '../../../servicos/erros';
import {
  CHAVE_DA_PROSPECCAO,
  OPCOES_DE_MES,
  importarColeta,
  mesDoNomeDoArquivo,
  rotuloDoMes,
  type ColetaResumo,
} from '../../../servicos/prospeccao';
import { ErroDaApi } from '../../ErroDaApi';

/**
 * Importar a planilha "Mailing extrato SISCRED" de um mês.
 *
 * O mês vem do nome do arquivo (a automação o nomeia com a data da coleta) e
 * pode ser corrigido. Mês que já tem coleta pede confirmação para substituir:
 * os saldos são trocados, as anotações das empresas ficam.
 */
export function ImportarColeta(props: {
  readonly aoFechar: () => void;
  readonly aoImportar: (coleta: ColetaResumo) => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const dialogo = useDialogo();
  const hoje = new Date();
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [ano, setAno] = useState(String(hoje.getFullYear()));
  const [mes, setMes] = useState(String(hoje.getMonth() + 1));

  const anos = Array.from({ length: 7 }, (_, i) => String(hoje.getFullYear() + 1 - i));

  const importar = useMutation({
    mutationFn: async (substituir: boolean) => {
      if (arquivo === null) throw new Error('Escolha a planilha.');
      return importarColeta({
        nomeArquivo: arquivo.name,
        conteudoBase64: await lerComoBase64(arquivo),
        ano: Number(ano),
        mes: Number(mes),
        substituir,
      });
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: CHAVE_DA_PROSPECCAO }),
    onError: async (erro, substituir) => {
      if (substituir || !ehMesOcupado(erro)) return;
      const ok = await dialogo.confirmar({
        titulo: `Substituir a coleta de ${rotuloDoMes(Number(ano), Number(mes))}?`,
        descricao: `${erro.message.replace(/ Confirme para substituí-la\.$/, '')}. Os saldos do mês passam a ser os desta planilha; as anotações das empresas (contatos, deságio, atualizações) ficam como estão.`,
        rotuloConfirmar: 'Substituir',
        tom: 'perigo',
      });
      if (ok) importar.mutate(true);
    },
  });

  const resultado = importar.data;

  return (
    <Modal
      aberto
      aoFechar={props.aoFechar}
      titulo="Importar planilha do SISCRED"
      descricao='A planilha "Mailing extrato SISCRED" que a automação gera todo mês.'
      largura="larga"
      rodape={
        resultado === undefined ? (
          <>
            <Botao variante="secundario" onClick={props.aoFechar}>
              Cancelar
            </Botao>
            <Botao
              disabled={arquivo === null}
              carregando={importar.isPending}
              onClick={() => importar.mutate(false)}
            >
              Importar
            </Botao>
          </>
        ) : (
          <Botao onClick={() => props.aoImportar(resultado.coleta)}>Ver a coleta</Botao>
        )
      }
    >
      {resultado !== undefined ? (
        <div className="flex flex-col gap-4">
          <Aviso tom="positivo" titulo="Coleta importada">
            {resultado.coleta.empresas} empresas em{' '}
            {rotuloDoMes(resultado.coleta.ano, resultado.coleta.mes)}, somando{' '}
            {formatarMoeda(resultado.coleta.creditoSomado)} de crédito.
            {resultado.anotacoesDaPlanilha > 0 &&
              ` ${resultado.anotacoesDaPlanilha} ${resultado.anotacoesDaPlanilha === 1 ? 'empresa ficou' : 'empresas ficaram'} com as anotações da planilha (contatos, deságio, atualizações).`}
            {resultado.anotacoesMantidas > 0 &&
              ` ${resultado.anotacoesMantidas} ${resultado.anotacoesMantidas === 1 ? 'anotação editada' : 'anotações editadas'} no Deca ${resultado.anotacoesMantidas === 1 ? 'foi mantida' : 'foram mantidas'} — a planilha só completou o que estava vazio.`}
          </Aviso>
          {resultado.avisos.length > 0 && (
            <Aviso
              tom="atencao"
              titulo={`${resultado.avisos.length} ${resultado.avisos.length === 1 ? 'linha ficou' : 'linhas ficaram'} de fora`}
            >
              <ul className="mt-1 flex max-h-48 list-disc flex-col gap-1 overflow-y-auto pl-5">
                {resultado.avisos.map((a) => (
                  <li key={a}>{a}</li>
                ))}
              </ul>
            </Aviso>
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <label className="flex flex-col gap-2">
            <span className="text-sm font-medium text-conteudo-primario">Planilha (.xlsx)</span>
            <input
              type="file"
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={(e) => {
                const escolhido = e.target.files?.[0] ?? null;
                setArquivo(escolhido);
                importar.reset();
                const doNome = escolhido === null ? null : mesDoNomeDoArquivo(escolhido.name);
                if (doNome !== null) {
                  setAno(String(doNome.ano));
                  setMes(String(doNome.mes));
                }
              }}
              className="text-sm text-conteudo-secundario file:mr-3 file:rounded-card file:border file:border-borda file:bg-superficie-base file:px-3 file:py-2 file:text-sm"
            />
          </label>

          <div className="grid gap-4 sm:grid-cols-2">
            <CampoDeSelecao
              rotulo="Mês da coleta"
              value={mes}
              onChange={(e) => setMes(e.target.value)}
              opcoes={OPCOES_DE_MES}
            />
            <CampoDeSelecao
              rotulo="Ano"
              value={ano}
              onChange={(e) => setAno(e.target.value)}
              opcoes={anos.map((a) => ({ valor: a, rotulo: a }))}
            />
          </div>
          <p className="-mt-2 text-xs text-conteudo-sutil">
            Preenchido pela data no nome do arquivo, quando ela existe. Confira antes de importar.
          </p>

          {importar.isError && !ehMesOcupado(importar.error) && (
            <ErroDaApi titulo="Não foi possível importar" erro={importar.error} />
          )}
        </div>
      )}
    </Modal>
  );
}

/** 409: o mês já tem coleta — vira a pergunta "substituir?", não erro na tela. */
function ehMesOcupado(erro: unknown): erro is ErroApi {
  return erro instanceof ErroApi && erro.status === 409;
}
